import { useCallback, useState } from 'react';
import { Building2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { complete_onboarding_api } from '../../DAL/Login/Login';
import { PORTAL_TYPE_UI_OPTIONS } from '../../utils/portalTypeUi';

export default function Welcome() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(null);
  const [portalTypes, setPortalTypes] = useState([]);

  const togglePortalType = useCallback((value) => {
    setPortalTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }, []);

  const handleChoice = useCallback(
    async (userType) => {
      if (userType === 'associate' && portalTypes.length === 0) {
        enqueueSnackbar(t('welcome.associate.capabilitiesRequired'), { variant: 'error' });
        return;
      }

      setSubmitting(userType);
      try {
        const payload =
          userType === 'associate'
            ? { user_type: userType, portal_types: portalTypes }
            : { user_type: userType };
        const result = await complete_onboarding_api(payload);
        if (result.code !== 200) {
          throw new Error(result.message || t('welcome.submitFailed'));
        }
        // Onboarding is done on the server; product session still caches the
        // pre-onboarding workspace (sessionEpoch unchanged). Reload so member_init
        // runs fresh — same outcome as the manual refresh that already works.
        window.location.assign('/dashboard');
      } catch (err) {
        const message =
          err instanceof Error && err.message ? err.message : t('welcome.submitFailed');
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(null);
      }
    },
    [enqueueSnackbar, portalTypes, t],
  );

  return (
    <div className="welcome-dashboard-content">
      <section className="portal-hero portal-hero--compact">
        <div className="portal-hero-badge">{t('welcome.badge')}</div>
        <h1>{t('welcome.title')}</h1>
        <p className="welcome-intro">{t('welcome.intro')}</p>
      </section>

      <section className="portal-section welcome-choices">
        <article className="portal-feature welcome-choice-card">
          <div className="portal-feature-icon">
            <Building2 size={24} aria-hidden="true" />
          </div>
          <h3>{t('welcome.associate.title')}</h3>
          <p>{t('welcome.associate.description')}</p>
          <ul className="welcome-choice-list">
            <li>{t('welcome.associate.point1')}</li>
            <li>{t('welcome.associate.point2')}</li>
            <li>{t('welcome.associate.point3')}</li>
          </ul>

          <div className="welcome-capabilities">
            <p className="welcome-capabilities-legend">{t('welcome.associate.capabilitiesLabel')}</p>
            <div className="welcome-capabilities-list" role="group" aria-label={t('welcome.associate.capabilitiesLabel')}>
              {PORTAL_TYPE_UI_OPTIONS.map((opt) => {
                const selected = portalTypes.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`welcome-capability${selected ? ' is-selected' : ''}`}
                    aria-pressed={selected}
                    disabled={Boolean(submitting)}
                    onClick={() => togglePortalType(opt.value)}
                  >
                    {t(opt.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className="portal-btn portal-btn--primary"
            disabled={Boolean(submitting) || portalTypes.length === 0}
            onClick={() => handleChoice('associate')}
          >
            {submitting === 'associate' ? t('welcome.settingUp') : t('welcome.associate.cta')}
          </button>
        </article>

        <article className="portal-feature welcome-choice-card">
          <div className="portal-feature-icon">
            <Users size={24} aria-hidden="true" />
          </div>
          <h3>{t('welcome.client.title')}</h3>
          <p>{t('welcome.client.description')}</p>
          <ul className="welcome-choice-list">
            <li>{t('welcome.client.point1')}</li>
            <li>{t('welcome.client.point2')}</li>
            <li>{t('welcome.client.point3')}</li>
          </ul>
          <button
            type="button"
            className="portal-btn portal-btn--ghost"
            disabled={Boolean(submitting)}
            onClick={() => handleChoice('client')}
          >
            {submitting === 'client' ? t('welcome.settingUp') : t('welcome.client.cta')}
          </button>
        </article>
      </section>

      <p className="welcome-footnote">{t('welcome.footnote')}</p>
    </div>
  );
}
